/* eslint-disable implicit-arrow-linebreak */
import Vue from 'vue';
import VueRouter from 'vue-router';
import {
  getRequiredPermissions,
  hasAnyStoredPermission,
} from '@/permissions/helpers';
import requireAuth from './auth';

Vue.use(VueRouter);

const featureRouteMatchers = [
  { feature: 'rental_checklists', match: (path) => path.startsWith('/rental-contracts/checklists') },
  { feature: 'rental_contracts', match: (path) => path.startsWith('/rental-contracts') },
  { feature: 'maintenance_alerts', match: (path) => path.startsWith('/maintenance-alerts') },
  { feature: 'warehouse', match: (path) => path.startsWith('/warehouse') },
  { feature: 'providers', match: (path) => path.startsWith('/provider') },
  { feature: 'specialists', match: (path) => path.startsWith('/specialists') },
  { feature: 'drivers', match: (path) => path.startsWith('/drivers') },
  { feature: 'clients', match: (path) => path.startsWith('/clients') },
  { feature: 'coupleds', match: (path) => path.startsWith('/coupleds') },
  { feature: 'users', match: (path) => path.startsWith('/users') },
  { features: ['reports', 'documentation_alerts'], match: (path) => path === '/reportes/documentation-alerts' },
  { features: ['reports', 'equipment_assignment'], match: (path) => path === '/reportes/equipment-assignment' },
  { feature: 'reports', match: (path) => path.startsWith('/reportes') },
  { feature: 'inventory', match: (path) => path.startsWith('/inventario') || path.startsWith('/insumo') || path.startsWith('/nuevo-insumo') },
  { features: ['trucks', 'trucks_documents'], match: (path) => /^\/truck\/[^/]+\/documentation$/.test(path) },
  { feature: 'trucks', match: (path) => path.startsWith('/truck') },
  { feature: 'work_orders', match: (path) => path.startsWith('/ordenes-de-trabajo') || path.startsWith('/historial-ordenes-de-trabajo') },
  { feature: 'procurement_purchase_orders', match: (path) => path.startsWith('/ordenes-de-compra') },
];

const defaultRoutesByFeature = {
  work_orders: '/ordenes-de-trabajo',
  inventory: '/inventario',
  trucks: '/truck',
  reports: '/reportes/expenses-by-truck',
  warehouse: '/warehouse',
  procurement_purchase_orders: '/ordenes-de-compra',
  maintenance_alerts: '/maintenance-alerts',
  providers: '/provider',
  specialists: '/specialists',
  drivers: '/drivers',
  clients: '/clients',
  rental_contracts: '/rental-contracts',
  rental_checklists: '/rental-contracts/checklists',
  coupleds: '/coupleds',
  users: '/users',
};

function getFeatureFlags() {
  try {
    return JSON.parse(localStorage.getItem('feature_flags') || '{}');
  } catch (error) {
    return {};
  }
}

function getRequiredFeature(path) {
  const targetMatcher = featureRouteMatchers.find(({ match }) => match(path));
  return targetMatcher ? (targetMatcher.features || targetMatcher.feature) : null;
}

function getFirstEnabledRoute() {
  const flags = getFeatureFlags();
  const priority = [
    'work_orders',
    'inventory',
    'trucks',
    'reports',
    'warehouse',
    'procurement_purchase_orders',
    'maintenance_alerts',
    'providers',
    'specialists',
    'drivers',
    'clients',
    'rental_contracts',
    'rental_checklists',
    'coupleds',
    'users',
  ];

  const enabledFeature = priority.find((feature) => flags[feature] !== false);

  return enabledFeature ? defaultRoutesByFeature[enabledFeature] : '/login';
}

const routes = [
  {
    path: '/',
    redirect: {
      name: 'Ordenes de trabajo',
    },
    component: () => import(/* webpackChunkName: "main" */ '../layouts/default/DefaultView.vue'),
    children: [
      {
        path: '/ordenes-de-compra',
        name: 'Ordenes de compra',
        meta: { requiresAuth: true },
        component: () => import('../procurement/views/PurchaseOrdersView.vue'),
      },
      {
        path: '/ordenes-de-compra/crear',
        name: 'Crear orden de compra',
        meta: { requiresAuth: true },
        component: () => import('../procurement/views/CreatePurchaseOrderView.vue'),
      },
      {
        path: '/ordenes-de-compra/:id',
        name: 'Detalle orden de compra',
        meta: { requiresAuth: true },
        component: () => import('../procurement/views/PurchaseOrderDetailView.vue'),
      },
      {
        path: 'ordenes-de-trabajo',
        name: 'Ordenes de trabajo',
        meta: {
          requiresAuth: true,
        },
        component: () => import(/* webpackChunkName: "ots" */ '../worker_order/views/OtsView.vue'),
      },
      {
        path: 'historial-ordenes-de-trabajo',
        name: 'Historial Ots',
        meta: {
          requiresAuth: true,
        },
        component: () =>
          import(/* webpackChunkName: "ots-history" */ '../worker_order/views/HistoryOtsView.vue'),
      },
      {
        path: 'reportes',
        name: 'Reportes',
        meta: {
          requiresAuth: true,
        },
        component: () =>
          import(/* webpackChunkName: "ots-history" */ '../reports/views/IndexReport.vue'),
        children: [
          {
            path: 'expenses-by-truck',
            name: 'Reportes',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(
                /* webpackChunkName: "expenses-by-suppliers" */
                '../reports/views/ListReportsView.vue'
              ),
          },
          {
            path: 'expenses-by-suppliers',
            name: 'Reportes',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(
                /* webpackChunkName: "expenses-by-suppliers" */
                '../reports/views/ExpensesBySuppliersView.vue'
              ),
          },
          {
            path: 'historical-expenses',
            name: 'Reportes',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(
                /* webpackChunkName: "expenses-by-suppliers" */
                '../reports/views/HistoricalExpensesView.vue'
              ),
          },
          {
            path: 'expenses-by-category',
            name: 'Reportes',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(
                /* webpackChunkName: "expenses-by-suppliers" */
                '../reports/views/ExpensesByCategoryView.vue'
              ),
          },
          {
            path: 'expenses-by-month',
            name: 'Reportes',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(
                /* webpackChunkName: "expenses-by-suppliers" */
                '../reports/views/ExpensesByMonth.vue'
              ),
          },
          {
            path: 'equipment-availability',
            name: 'Reportes',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(
                /* webpackChunkName: "expenses-by-suppliers" */
                '../reports/views/EquipmentAvailability.vue'
              ),
          },
          {
            path: 'available-equipment',
            name: 'Equipos disponibles',
            meta: { requiresAuth: true },
            component: () => import(
              /* webpackChunkName: "available-equipment" */
              '../reports/views/AvailableEquipmentView.vue'
            ),
          },
          {
            path: 'documentation-alerts',
            name: 'Reportes',
            meta: {
              requiresAuth: true,
            },
            component: () => import(
              /* webpackChunkName: "documentation-alerts" */
              '../reports/views/DocumentationAlerts.vue'
            ),
          },
          {
            path: 'equipment-assignment',
            name: 'Reportes',
            meta: {
              requiresAuth: true,
            },
            component: () => import(
              /* webpackChunkName: "equipment-assignment" */
              '../reports/views/EquipmentAssignmentView.vue'
            ),
          },
          // You can add more child routes as needed
        ],
      },
      {
        path: 'inventario',
        name: 'Inventario',
        meta: {
          requiresAuth: true,
        },
        component: () =>
          import(/* webpackChunkName: "supplies" */ '../inventory/views/InventoryView.vue'),
      },
      {
        path: '/insumo/:id',
        name: 'inventario insumos',
        meta: {
          requiresAuth: true,
        },
        props: true,
        component: () =>
          import(/* webpackChunkName: "supplies" */ '../inventory/views/ShowSupplies.vue'),
      },
      {
        path: '/nuevo-insumo',
        name: 'inventario crear insumo',
        meta: {
          requiresAuth: true,
        },
        component: () =>
          import(/* webpackChunkName: "supplies" */ '../inventory/views/CreateSupplies.vue'),
      },
      {
        path: '/warehouse',
        meta: {
          requiresAuth: true,
        },
        component: () =>
          import(/* webpackChunkName: "supplies" */ '../warehouse/views/WarehouseView.vue'),
        children: [
          {
            path: '/',
            name: 'Listar bodega',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(
                /* webpackChunkName: "warehouse" */
                '../warehouse/views/ListWarehouseView.vue'
              ),
          },
          {
            path: 'create',
            name: 'Crear bodega',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(
                /* webpackChunkName: "warehouse" */
                '../warehouse/views/CreateWarehouseView.vue'
              ),
          },
          {
            path: '/warehouse/:id',
            name: 'Ver bodega',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(
                /* webpackChunkName: "warehouse" */
                '../warehouse/views/ContainerWarehouseView.vue'
              ),
            children: [
              {
                path: '',
                name: 'General',
                meta: {
                  requiresAuth: true,
                },
                component: () =>
                  import(
                    /* webpackChunkName: "warehouse" */
                    '../warehouse/components/FormWarehouse.vue'
                  ),
              },
              {
                path: 'stock',
                name: 'Inventario',
                meta: {
                  requiresAuth: true,
                },
                component: () =>
                  import(/* webpackChunkName: "warehouse" */ '../warehouse/views/StockView.vue'),
              },
              {
                path: 'purchases-orders',
                name: 'Ordenes de compra',
                meta: {
                  requiresAuth: true,
                },
                component: () =>
                  import(
                    /* webpackChunkName: "warehouse" */
                    '../warehouse/views/PurchaseOrdersView.vue'
                  ),
              },
              {
                path: 'purchases',
                name: 'Entradas',
                meta: {
                  requiresAuth: true,
                },
                component: () =>
                  import(
                    /* webpackChunkName: "warehouse" */
                    '../warehouse/views/PurchasesView.vue'
                  ),
              },
              {
                path: 'sales',
                name: 'Salidas',
                meta: {
                  requiresAuth: true,
                },
                component: () =>
                  import(/* webpackChunkName: "warehouse" */ '../warehouse/views/SalesView.vue'),
              },
              {
                path: 'movement-orders',
                name: 'Historial de movimeintos',
                meta: {
                  requiresAuth: true,
                },
                component: () =>
                  import(
                    /* webpackChunkName: "warehouse" */
                    '../warehouse/views/MovementHistory.vue'
                  ),
              },
            ],
          },
        ],
      },
      {
        path: '/maintenance-alerts',
        meta: {
          requiresAuth: true,
        },
        component: () =>
          import(/* webpackChunkName: "supplies" */ '../maintenance-alerts/views/AlertsView.vue'),
        children: [
          {
            path: '/',
            name: 'Listar Alertas',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(
                /* webpackChunkName: "warehouse" */
                '../maintenance-alerts/views/ListAlertsView.vue'
              ),
          },
        ],
      },
      {
        path: '/provider',
        meta: {
          requiresAuth: true,
        },
        component: () =>
          import(/* webpackChunkName: "supplies" */ '../provider/views/ProviderView.vue'),
        children: [
          {
            path: '/',
            name: 'Proveedores',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(/* webpackChunkName: "warehouse" */ '../provider/views/ListProviderView.vue'),
          },
          {
            path: 'create',
            name: 'Crear proveedor',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(/* webpackChunkName: "warehouse" */ '../provider/views/CreateProviderView.vue'),
          },
          {
            path: ':id',
            name: 'Ver proveedor',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(/* webpackChunkName: "warehouse" */ '../provider/views/ContainerProviderView.vue'),
            children: [
              {
                path: '',
                name: 'General',
                meta: {
                  requiresAuth: true,
                },
                component: () =>
                  import(
                    /* webpackChunkName: "warehouse" */
                    '../provider/components/FormProvider.vue'
                  ),
              },
              {
                path: 'services',
                name: 'Servicios',
                meta: {
                  requiresAuth: true,
                },
                component: () =>
                  import(/* webpackChunkName: "warehouse" */ '../provider/views/ServicesView.vue'),
              },
            ],
          },
        ],
      },
      {
        path: '/specialists',
        meta: {
          requiresAuth: true,
        },
        component: () =>
          import(/* webpackChunkName: "specialists" */ '../specialists/views/SpecialistView.vue'),
        children: [
          {
            path: '/',
            name: 'Especialistas',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(
                /* webpackChunkName: "specialists" */
                '../specialists/views/ListSpecialistView.vue'
              ),
          },
          {
            path: 'create',
            name: 'Crear especialista',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(
                /* webpackChunkName: "specialists" */
                '../specialists/views/CreateSpecialistView.vue'
              ),
            children: [
              {
                path: '',
                name: 'General especialista',
                meta: {
                  requiresAuth: true,
                },
                component: () =>
                  import(
                    /* webpackChunkName: "specialists" */
                    '../specialists/components/FormSpecialist.vue'
                  ),
              },
            ],
          },
          {
            path: ':id',
            name: 'Ver especialista',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(
                /* webpackChunkName: "specialists" */
                '../specialists/views/ContainerSpecialistView.vue'
              ),
            children: [
              {
                path: '',
                name: 'General',
                meta: {
                  requiresAuth: true,
                },
                component: () =>
                  import(
                    /* webpackChunkName: "specialists" */
                    '../specialists/components/FormSpecialist.vue'
                  ),
              },
            ],
          },
        ],
      },
      {
        path: '/drivers',
        meta: {
          requiresAuth: true,
        },
        component: () => import(/* webpackChunkName: "drivers" */
          '../drivers/views/DriverView.vue'
        ),
        children: [
          {
            path: '/',
            name: 'Conductores',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "drivers" */
              '../drivers/views/ListDriverView.vue'
            ),
          },
          {
            path: '/drivers/create',
            name: 'Crear conductor',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "drivers" */
              '../drivers/views/CreateDriverView.vue'
            ),
            children: [
              {
                path: '',
                name: 'General',
                meta: {
                  requiresAuth: true,
                },
                component: () => import(/* webpackChunkName: "drivers" */
                  '../drivers/components/FormDriver.vue'
                ),
              },
            ],
          },
          {
            path: '/drivers/:id',
            name: 'Ver conductor',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "drivers" */
              '../drivers/views/ContainerDriverView.vue'
            ),
            children: [
              {
                path: '',
                name: 'General',
                meta: {
                  requiresAuth: true,
                },
                component: () => import(/* webpackChunkName: "drivers" */
                  '../drivers/components/FormDriver.vue'
                ),
              },
            ],
          },
        ],
      },
      {
        path: '/clients',
        meta: {
          requiresAuth: true,
        },
        component: () => import(/* webpackChunkName: "clients" */
          '../clients/views/ClientView.vue'
        ),
        children: [
          {
            path: '/',
            name: 'Clientes',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "clients" */
              '../clients/views/ListClientView.vue'
            ),
          },
          {
            path: '/clients/create',
            name: 'Crear cliente',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "clients" */
              '../clients/views/CreateClientView.vue'
            ),
            children: [
              {
                path: '',
                name: 'General cliente',
                meta: {
                  requiresAuth: true,
                },
                component: () => import(/* webpackChunkName: "clients" */
                  '../clients/components/FormClient.vue'
                ),
              },
            ],
          },
          {
            path: '/clients/:id',
            name: 'Ver cliente',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "clients" */
              '../clients/views/ContainerClientView.vue'
            ),
            children: [
              {
                path: '',
                name: 'Detalle cliente',
                meta: {
                  requiresAuth: true,
                },
                component: () => import(/* webpackChunkName: "clients" */
                  '../clients/components/FormClient.vue'
                ),
              },
              {
                path: 'faenas',
                name: 'Faenas cliente',
                meta: {
                  requiresAuth: true,
                },
                component: () => import(/* webpackChunkName: "clients" */
                  '../clients/views/ClientFaenasView.vue'
                ),
              },
              {
                path: 'history',
                name: 'Historial cliente',
                meta: {
                  requiresAuth: true,
                },
                component: () => import(/* webpackChunkName: "clients" */
                  '../clients/views/ClientHistoryView.vue'
                ),
              },
            ],
          },
        ],
      },
      {
        path: '/rental-contracts',
        meta: {
          requiresAuth: true,
        },
        component: () => import(/* webpackChunkName: "rental-contracts" */
          '../rental_contracts/views/RentalContractView.vue'
        ),
        children: [
          {
            path: '/',
            name: 'Contratos',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "rental-contracts" */
              '../rental_contracts/views/ListRentalContractView.vue'
            ),
          },
          {
            path: 'create',
            name: 'Crear contrato',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "rental-contracts" */
              '../rental_contracts/views/CreateRentalContractView.vue'
            ),
          },
          {
            path: 'checklist-questions',
            name: 'Preguntas checklist contrato',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "rental-contracts" */
              '../rental_contracts/views/ListRentalChecklistQuestionView.vue'
            ),
          },
          {
            path: 'checklists',
            name: 'Checklist',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "rental-contracts" */
              '../rental_contracts/views/ListRentalChecklistTemplateView.vue'
            ),
          },
          {
            path: 'checklists/create',
            name: 'Crear checklist base',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "rental-contracts" */
              '../rental_contracts/views/CreateRentalChecklistTemplateView.vue'
            ),
          },
          {
            path: 'checklists/:id',
            name: 'Ver checklist base',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "rental-contracts" */
              '../rental_contracts/views/CreateRentalChecklistTemplateView.vue'
            ),
          },
          {
            path: ':id/equipments',
            name: 'Equipos contrato',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "rental-contracts" */
              '../rental_contracts/views/CreateRentalContractView.vue'
            ),
          },
          {
            path: ':id/checklists',
            name: 'Checklists contrato',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "rental-contracts" */
              '../rental_contracts/views/CreateRentalContractView.vue'
            ),
          },
          {
            path: ':id/history',
            name: 'Historial contrato',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "rental-contracts" */
              '../rental_contracts/views/CreateRentalContractView.vue'
            ),
          },
          {
            path: ':id/documents',
            name: 'Documentos contrato',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "rental-contracts" */
              '../rental_contracts/views/CreateRentalContractView.vue'
            ),
          },
          {
            path: ':id/delivery-certificate',
            name: 'Acta de entrega contrato',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "rental-contracts" */
              '../rental_contracts/views/CreateRentalContractView.vue'
            ),
          },
          {
            path: ':id/reception-certificate',
            name: 'Acta de recepcion contrato',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "rental-contracts" */
              '../rental_contracts/views/CreateRentalContractView.vue'
            ),
          },
          {
            path: ':id',
            name: 'Ver contrato',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "rental-contracts" */
              '../rental_contracts/views/CreateRentalContractView.vue'
            ),
          },
        ],
      },
      {
        path: '/coupleds',
        meta: {
          requiresAuth: true,
        },
        component: () => import(/* webpackChunkName: "coupleds" */
          '../coupleds/views/CoupledView.vue'
        ),
        children: [
          {
            path: '/',
            name: 'Carros',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "coupleds" */
              '../coupleds/views/ListCoupledView.vue'
            ),
          },
          {
            path: '/coupleds/create',
            name: 'Crear carro',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "coupleds" */
              '../coupleds/views/CreateCoupledView.vue'
            ),
            children: [
              {
                path: '',
                name: 'General',
                meta: {
                  requiresAuth: true,
                },
                component: () => import(/* webpackChunkName: "coupleds" */
                  '../coupleds/components/FormCoupled.vue'
                ),
              },
            ],
          },
          {
            path: '/coupleds/:id',
            name: 'Ver carro',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "coupleds" */
              '../coupleds/views/ContainerCoupledView.vue'
            ),
            children: [
              {
                path: '',
                name: 'General',
                meta: {
                  requiresAuth: true,
                },
                component: () => import(/* webpackChunkName: "coupleds" */
                  '../coupleds/components/FormCoupled.vue'
                ),
              },
            ],
          },
        ],
      },
      {
        path: '/users',
        meta: {
          requiresAuth: true,
        },
        component: () => import(/* webpackChunkName: "users" */
          '../users/views/UserView.vue'
        ),
        children: [
          {
            path: '/',
            name: 'Usuarios',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "users" */
              '../users/views/ListUserView.vue'
            ),
          },
          {
            path: '/users/create',
            name: 'Crear usuario',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "users" */
              '../users/views/CreateUserView.vue'
            ),
            children: [
              {
                path: '',
                name: 'General',
                meta: {
                  requiresAuth: true,
                },
                component: () => import(/* webpackChunkName: "users" */
                  '../users/components/FormUser.vue'
                ),
              },
            ],
          },
          {
            path: '/users/:id',
            name: 'Ver usuario',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "users" */
              '../users/views/ContainerUserView.vue'
            ),
            children: [
              {
                path: '',
                name: 'General',
                meta: {
                  requiresAuth: true,
                },
                component: () => import(/* webpackChunkName: "users" */
                  '../users/components/FormUser.vue'
                ),
              },
            ],
          },
        ],
      },
      {
        path: '/truck',
        meta: {
          requiresAuth: true,
        },
        component: () => import(/* webpackChunkName: "supplies" */
          '../trucks/views/TruckView.vue'
        ),
        children: [
          {
            path: '/',
            name: 'Equipos',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "warehouse" */
              '../trucks/views/ListTruckView.vue'
            ),
          },
          {
            path: '/truck/create',
            name: 'Crear equipo',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "warehouse" */
              '../trucks/views/CreateTruckView.vue'
            ),
            children: [
              {
                path: '',
                name: 'General',
                meta: {
                  requiresAuth: true,
                },
                component: () => import(/* webpackChunkName: "warehouse" */
                  '../trucks/components/FormTruck.vue'
                ),
              },
            ],
          },
          {
            path: '/truck/:id',
            name: 'Ver equipo',
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "warehouse" */
              '../trucks/views/ContainerTruckView.vue'
            ),
            children: [
              {
                path: '',
                name: 'General',
                meta: {
                  requiresAuth: true,
                },
                component: () => import(/* webpackChunkName: "warehouse" */
                  '../trucks/components/FormTruck.vue'
                ),
              },
              {
                path: 'documentation',
                name: 'Documentación',
                meta: {
                  requiresAuth: true,
                },
                component: () => import(
                  /* webpackChunkName: "warehouse" */
                  '@/trucks/views/TruckDocumentationView.vue'
                ),
              },
            ],
          },
          {
            path: '*',
            name: 'not-found',
            meta: {
              requiresAuth: true,
            },
            component: () =>
              import(/* webpackChunkName: "not-found" */ '../views/NotFoundView.vue'),
          },
        ],
      },
      // {
      //   path: '/settings',
      //   meta: {
      //     requiresAuth: true,
      //   },
      //   component: () =>
      //     import(/* webpackChunkName: "supplies" */ '../settings/views/SettingView.vue'),
      //   children: [
      //     {
      //       path: 'settings/users',
      //       name: 'Crear proveedor',
      //       meta: {
      //         requiresAuth: true,
      //       },
      //       component: () =>
      //         import(
      //           /* webpackChunkName: "warehouse" */
      //           '../users/views/ContainerUserList.vue'
      //         ),
      //     },
      //   ],
      // },
    ],
  },
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "login" */ '@/auth/view/AuthView.vue'),
  },
  {
    path: '/network-error',
    name: 'network-error',
    component: () => import('../views/NetworkErrorView.vue'),
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('../views/MaintenanceView.vue'),
  },
  {
    path: '/403',
    name: 'forbidden',
    component: () => import(/* webpackChunkName: "forbidden" */ '../views/ForbiddenView.vue'),
  },
  {
    path: '*',
    name: 'not-found-root',
    meta: {
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "not-found" */ '../views/NotFoundView.vue'),
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  if (['/login', '/403', '/network-error', '/maintenance'].includes(to.path)) {
    next();
    return;
  }

  requireAuth(to, from, (authResult) => {
    if (typeof authResult === 'string') {
      next(authResult);
      return;
    }

    const requiredFeature = getRequiredFeature(to.path);

    if (!requiredFeature) {
      next();
      return;
    }

    const featureFlags = getFeatureFlags();
    const requiredFeatures = Array.isArray(requiredFeature) ? requiredFeature : [requiredFeature];

    if (requiredFeatures.some((feature) => feature && featureFlags[feature] === false)) {
      next(getFirstEnabledRoute());
      return;
    }

    const requiredPermissions = getRequiredPermissions(to.path);

    if (requiredPermissions.length && !hasAnyStoredPermission(requiredPermissions)) {
      sessionStorage.setItem('forbidden_error', JSON.stringify({
        message: 'No tienes permisos para acceder a esta ruta.',
        code: 'missing_route_permission',
      }));
      next('/403');
      return;
    }

    next();
  });
});

export default router;
